/**
 * Supabase client configuration for VisualGrid
 * Handles authentication and database operations
 * 
 * ⚠️ IMPORTANT: Le client Supabase peut devenir "corrompu" après une période
 * d'inactivité de l'onglet (bug connu Supabase #36046). Ce fichier fournit
 * un mécanisme pour recréer le client quand nécessaire.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  // console.warn('⚠️ Supabase credentials missing. Auth features will be disabled.');
}

/**
 * Configuration du client Supabase
 */
const getSupabaseConfig = () => ({
  auth: {
    persistSession: true,
    autoRefreshToken: typeof navigator === 'undefined' ? true : navigator.onLine,
    detectSessionInUrl: true,
    flowType: 'pkce' as const,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  realtime: {
    heartbeatIntervalMs: 15000,
    heartbeatCallback: (status: string) => {
      if (status === 'timeout' || status === 'disconnected' || status === 'error') {
        // console.warn('⚠️ [Realtime] Heartbeat issue:', status)
      }
    },
  },
});

/**
 * Client Supabase principal (mutable pour permettre la recréation)
 */
let _supabaseClient: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, getSupabaseConfig());
let _clientVersion = 0;
let _lastRecreationTime = 0;
const RECREATION_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Getter pour le client Supabase actuel
 */
export const supabase = new Proxy({} as SupabaseClient, {
  get(_, prop) {
    return (_supabaseClient as any)[prop];
  }
});

/**
 * Recréer le client Supabase (solution au bug #36046)
 * À appeler quand le client devient non-responsive après inactivité
 */
export async function recreateSupabaseClient(): Promise<void> {
  const now = Date.now();
  if (now - _lastRecreationTime < RECREATION_COOLDOWN_MS) {
    return;
  }
  _lastRecreationTime = now;

  try {
    // ⚠️ Ne PAS attendre disconnect() - ça peut bloquer sur un client corrompu!
    // On lance juste la déconnexion sans attendre
    try {
      // Timeout très court pour disconnect - on n'attend pas
      const disconnectPromise = _supabaseClient.realtime.disconnect()
      const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 100))
      await Promise.race([disconnectPromise, timeoutPromise])
    } catch (e) {
      // Ignorer les erreurs de déconnexion
    }
    
    // Créer un nouveau client IMMÉDIATEMENT
    _supabaseClient = createClient(supabaseUrl, supabaseAnonKey, getSupabaseConfig());
    _clientVersion++;
    
    // console.log('✅ [Supabase] Client recréé (version', _clientVersion, ')')
    
    // Reconnecter Realtime (non-bloquant)
    try {
      _supabaseClient.realtime.connect()
    } catch (e) {
      // console.warn('⚠️ [Supabase] Erreur connexion Realtime:', e)
    }
    
  } catch (error) {
    // console.error('❌ [Supabase] Erreur lors de la recréation du client:', error)
    throw error
  }
}

/**
 * Vérifier si le client est responsive avec un timeout
 */
export async function isSupabaseClientHealthy(timeoutMs = 3000): Promise<boolean> {
  try {
    const healthCheck = _supabaseClient.auth.getSession()
    const timeout = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), timeoutMs)
    })
    
    const result = await Promise.race([healthCheck, timeout])
    return result !== null
  } catch {
    return false
  }
}

/**
 * Obtenir un client Supabase sain (recrée si nécessaire)
 */
export async function getHealthySupabaseClient(): Promise<SupabaseClient> {
  return _supabaseClient;
}

/**
 * Obtenir le client interne directement (pour les cas où le proxy ne suffit pas)
 */
export function getSupabaseClientDirect(): SupabaseClient {
  return _supabaseClient
}

/**
 * Database types (to be generated from Supabase schema)
 */
export interface Profile {
  id: string;
  email: string;
  display_name: string | null;
  plan: 'free' | 'pro';
  stripe_customer_id: string | null;
  preferred_language?: 'en' | 'fr';
  updated_at: string;
}

export interface Subscription {
  id: string;
  user_id: string;
  status: string;
  current_period_end: string;
  product_id: string | null;
  price_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  owner_id: string;
  name: string;
  created_at: string;
}

export interface ProjectMember {
  project_id: string;
  user_id: string;
  role: 'owner' | 'editor' | 'viewer';
}

export interface Configuration {
  id: string;
  project_id: string;
  name: string;
  data: Record<string, any>;
  version: number;
  updated_at: string;
}

